import { useState, useEffect } from "react"
import { Container, Row, Col, Button } from "react-bootstrap"
import {
  AlertCircle,
  BookOpen,
  ChevronLeft,
  CheckCircle,
  XCircle,
  RotateCcw,
  Brain,
  Target,
  Award,
  TrendingUp,
  Clock,
  Flag,
} from "lucide-react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { getMcqsByCourse } from "../../../../services/McqService"
import { toast } from '../../common/Toast/toast';
import { confirm } from '../../common/Confirm/confirm';

const StudentMcqView = () => {
  const [mcqs, setMcqs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentMcqIndex, setCurrentMcqIndex] = useState(0)
  const [selectedAnswers, setSelectedAnswers] = useState({})
  const [showAnswers, setShowAnswers] = useState(false)
  const [practiceMode, setPracticeMode] = useState(true)
  const [showScore, setShowScore] = useState(false)
  const [furthestReached, setFurthestReached] = useState(0) // Track furthest question reached
  const [timeLeft, setTimeLeft] = useState(3600) // 1 hour in seconds
  const [timerActive, setTimerActive] = useState(false)
  const [searchParams] = useSearchParams()
  const { courseId } = useParams()
  const navigate = useNavigate()
  const courseName = searchParams.get("courseName") || "Course"

  // Timer effect
  useEffect(() => {
    let interval = null
    if (timerActive && timeLeft > 0 && !showScore) {
      interval = setInterval(() => {
        setTimeLeft((timeLeft) => {
          if (timeLeft <= 1) {
            // Time's up - auto submit
            handleSubmit()
            toast.warning("Time's up! Quiz submitted automatically.")
            return 0
          }
          return timeLeft - 1
        })
      }, 1000)
    } else if (timeLeft === 0 || showScore) {
      clearInterval(interval)
      setTimerActive(false)
    }
    return () => clearInterval(interval)
  }, [timerActive, timeLeft, showScore])

  // Format time display
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
  }

  // Shuffle array function
  const shuffleArray = (array) => {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }

  const fetchMcqs = async () => {
    try {
      setLoading(true)
      const response = await getMcqsByCourse(courseId)
      let mcqsArray = []
      if (Array.isArray(response)) {
        mcqsArray = response
      } else if (response.data && Array.isArray(response.data)) {
        mcqsArray = response.data
      } else {
        console.error("Invalid MCQ response format:", response)
        throw new Error("Invalid response format")
      }

      if (mcqsArray.length === 0) {
        setError("No MCQs available for this course yet.")
      } else {
        // Randomly select 50 questions (or all if less than 50)
        const shuffledMcqs = shuffleArray(mcqsArray)
        const selectedMcqs = shuffledMcqs.slice(0, Math.min(50, shuffledMcqs.length))
        setMcqs(selectedMcqs)
        // Initialize selected answers object
        const initialAnswers = {}
        selectedMcqs.forEach((mcq, index) => {
          initialAnswers[index] = null
        })
        setSelectedAnswers(initialAnswers)
        // Start timer when MCQs are loaded
        setTimerActive(true)
      }
      setError(null)
    } catch (err) {
      setError("Failed to fetch MCQs. Please try again later.")
      console.error("Error fetching MCQs:", err)
      toast.error("Failed to load MCQs")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (courseId) {
      fetchMcqs()
    }
  }, [courseId])

  const handleAnswerSelect = (mcqIndex, selectedOption) => {
    // Only allow answer selection if answers are not being shown AND the question hasn't been answered yet
    if (!showAnswers && selectedAnswers[mcqIndex] === null) {
      setSelectedAnswers((prev) => ({
        ...prev,
        [mcqIndex]: selectedOption,
      }))
    }
  }

  const handleNextMcq = () => {
    if (currentMcqIndex < mcqs.length - 1) {
      const nextIndex = currentMcqIndex + 1
      setCurrentMcqIndex(nextIndex)
      // Update furthest reached
      if (nextIndex > furthestReached) {
        setFurthestReached(nextIndex)
      }
    }
  }

  const handlePreviousMcq = () => {
    if (currentMcqIndex > 0) {
      setCurrentMcqIndex(currentMcqIndex - 1)
    }
  }

  const handleMcqNavigation = (index) => {
    // Only allow navigation to questions that have been reached before
    if (index <= furthestReached) {
      setCurrentMcqIndex(index)
    }
  }

  const resetPractice = () => {
    const initialAnswers = {}
    mcqs.forEach((mcq, index) => {
      initialAnswers[index] = null
    })
    setSelectedAnswers(initialAnswers)
    setCurrentMcqIndex(0)
    setFurthestReached(0)
    setShowAnswers(false)
    setShowScore(false)
    setTimeLeft(3600) // Reset timer to 1 hour
    setTimerActive(true)
    toast.success("Practice session reset!")
  }

  const toggleAnswerVisibility = () => {
    setShowAnswers(!showAnswers)
  }

  const calculateScore = () => {
    let correct = 0
    mcqs.forEach((mcq, index) => {
      if (selectedAnswers[index] === mcq.correctAnswer) {
        correct++
      }
    })
    return { correct, total: mcqs.length, percentage: Math.round((correct / mcqs.length) * 100) }
  }

  const handleFinishTest = () => {
    confirm({
      title: "Finish Test?",
      text: "Are you sure you want to finish the test? This action cannot be undone.",
      confirmButtonText: "Yes, Finish Test",
      cancelButtonText: "Continue Test",
    }).then((result) => {
      if (result.isConfirmed) {
        handleSubmit()
      }
    })
  }

  const handleSubmit = () => {
    setShowAnswers(true)
    setShowScore(true)
    setTimerActive(false) // Stop timer
    const score = calculateScore()
    toast.success(`You scored ${score.percentage}%!`)
  }

  const getAnswerStatus = (mcqIndex, option) => {
    const selectedAnswer = selectedAnswers[mcqIndex]
    const correctAnswer = mcqs[mcqIndex]?.correctAnswer
    if (!showAnswers) return null
    if (option === correctAnswer) {
      return "correct"
    } else if (option === selectedAnswer && option !== correctAnswer) {
      return "incorrect"
    }
    return null
  }

  const getOptionClassName = (mcqIndex, option) => {
    const status = getAnswerStatus(mcqIndex, option)
    const isSelected = selectedAnswers[mcqIndex] === option
    let className = "option-card"
    if (showAnswers) {
      if (status === "correct") {
        className += " option-correct"
      } else if (status === "incorrect") {
        className += " option-incorrect"
      }
    } else if (isSelected) {
      className += " option-selected"
    }
    return className
  }

  if (loading) {
    return (
      <div className="loading-container">
        <Container fluid>
          <div className="loading-content">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
              <Brain size={40} className="brain-icon" />
            </div>
            <h3 className="loading-title">Preparing Your Learning Session</h3>
            <p className="loading-subtitle">Fetching practice questions to boost your knowledge</p>
            <div className="loading-progress">
              <div className="progress-bar"></div>
            </div>
          </div>
        </Container>
      </div>
    )
  }

  if (error) {
    return (
      <div className="error-container">
        <Container fluid>
          <div className="error-content">
            <div className="error-icon">
              <AlertCircle size={60} />
            </div>
            <h2 className="error-title">Oops! No Questions Found</h2>
            <p className="error-message">{error}</p>
            <div className="error-actions">
              <Button onClick={() => navigate("/student-courses")} className="btn-primary-custom">
                <ChevronLeft size={18} />
                Back to Courses
              </Button>
              <Button onClick={fetchMcqs} className="btn-outline-custom">
                <RotateCcw size={18} />
                Try Again
              </Button>
            </div>
          </div>
        </Container>
      </div>
    )
  }

  const currentMcq = mcqs[currentMcqIndex]
  const score = calculateScore()
  const answeredCount = Object.values(selectedAnswers).filter((answer) => answer !== null).length
  const allAnswered = answeredCount === mcqs.length

  return (
    <div className="mcq-container">      {/* Enhanced Header */}
      <div className="header-section">
        <Container>
          <Row className="align-items-center">
            <Col md={6}>
              <div className="header-content">
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => navigate("/student-courses")}
                  className="back-btn"
                >
                  <ChevronLeft size={16} />
                  Back
                </Button>
                <div className="course-info">
                  <h4 className="course-title">{courseName}</h4>
                  <p className="course-subtitle">
                    <Brain size={16} className="me-2" />
                    Interview Preparation • MCQ Practice
                  </p>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="header-stats">
                <div className="stat-item timer-display">
                  <Clock size={16} />
                  <span className={timeLeft <= 300 ? "timer-warning" : ""}>{formatTime(timeLeft)}</span>
                </div>
                <div className="stat-item">
                  <Target size={16} />
                  <span>
                    {currentMcqIndex + 1}/{mcqs.length}
                  </span>
                </div>
                <div className="stat-item">
                  <TrendingUp size={16} />
                  <span>{answeredCount} answered</span>
                </div>
                {showScore && (
                  <div className="stat-item score-badge">
                    <Award size={16} />
                    <span>{score.percentage}%</span>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="main-content">
        <Row className="g-4">
          {/* Enhanced Sidebar */}
          <Col lg={3}>
            <div className="sidebar-card">
              <div className="sidebar-header">
                <BookOpen size={20} />
                <h6>Question Navigator</h6>
              </div>
              <div className="progress-overview">
                <div className="progress-stats">
                  <div className="stat">
                    <span className="stat-number">{answeredCount}</span>
                    <span className="stat-label">Answered</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number">{mcqs.length - answeredCount}</span>
                    <span className="stat-label">Remaining</span>
                  </div>
                </div>
                <div className="progress-bar-container">
                  <div className="progress-bar-fill" style={{ width: `${(answeredCount / mcqs.length) * 100}%` }}></div>
                </div>
              </div>
              <div className="questions-grid">
                {mcqs.map((_, index) => {
                  const isAnswered = selectedAnswers[index] !== null
                  const isCurrent = index === currentMcqIndex
                  const isCorrect = showAnswers && selectedAnswers[index] === mcqs[index]?.correctAnswer
                  const isAccessible = index <= furthestReached
                  return (
                    <button
                      key={index}
                      className={`question-btn ${isCurrent ? "current" : ""} ${
                        isAnswered ? (showAnswers ? (isCorrect ? "correct" : "incorrect") : "answered") : "unanswered"
                      } ${!isAccessible ? "locked" : ""}`}
                      onClick={() => handleMcqNavigation(index)}
                      disabled={!isAccessible}
                    >
                      <span className="question-number">{index + 1}</span>
                      {showAnswers && isAnswered && (
                        <div className="question-status">
                          {isCorrect ? <CheckCircle size={12} /> : <XCircle size={12} />}
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
              <div className="sidebar-actions">
                <Button className="action-btn reset-btn" onClick={resetPractice}>
                  <RotateCcw size={16} />
                  Reset Practice
                </Button>
                {!showScore && (
                  <Button className="action-btn finish-btn" onClick={handleFinishTest}>
                    <Flag size={16} />
                    Finish Test
                  </Button>
                )}
                {allAnswered && !showScore && (
                  <Button className="action-btn submit-btn" onClick={handleSubmit}>
                    Submit Answers
                  </Button>
                )}
              </div>
            </div>
          </Col>

          {/* Enhanced Main Content */}
          <Col lg={9}>
            {showScore ? (
              <div className="score-card">
                <div className="score-header">
                  <Award size={32} />
                  <h3>Practice Results</h3>
                </div>
                <div className="score-content">
                  <div className="score-metric">
                    <div className="score-value">{score.percentage}%</div>
                    <div className="score-label">Overall Score</div>
                  </div>
                  <div className="score-details">
                    <div className="detail-item correct">
                      <CheckCircle size={16} />
                      <span>{score.correct} Correct</span>
                    </div>
                    <div className="detail-item incorrect">
                      <XCircle size={16} />
                      <span>{score.total - score.correct} Incorrect</span>
                    </div>
                    <div className="detail-item total">
                      <BookOpen size={16} />
                      <span>{score.total} Total Questions</span>
                    </div>
                  </div>
                  {/* MCQ Results List */}
                  <div className="results-list">
                    {mcqs.map((mcq, index) => {
                      const isCorrect = selectedAnswers[index] === mcq.correctAnswer
                      return (
                        <div key={index} className={`result-item ${isCorrect ? "correct" : "incorrect"}`}>
                          <div className="result-question">
                            <span className="result-number">Q{index + 1}:</span>
                            <span className="result-text">{mcq.question}</span>
                          </div>
                          <div className="result-answers">
                            <div className="result-answer">
                              <span className="answer-label">Your Answer:</span>
                              <span className={`answer-value ${!isCorrect ? "wrong" : ""}`}>
                                {selectedAnswers[index] || "Not answered"}
                              </span>
                            </div>
                            {!isCorrect && (
                              <div className="result-answer">
                                <span className="answer-label">Correct Answer:</span>
                                <span className="answer-value correct">{mcq.correctAnswer}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
                <div className="score-actions">
                  <Button className="action-btn restart-btn" onClick={resetPractice}>
                    <RotateCcw size={16} />
                    Restart Practice
                  </Button>
                </div>
              </div>
            ) : (
              <div className="question-card">
                <div className="question-header">
                  <div className="question-meta">
                    <span className="question-label">Question {currentMcqIndex + 1}</span>
                    <div className="difficulty-badge">
                      <Brain size={14} />
                      Practice Mode
                    </div>
                  </div>
                  {showAnswers && (
                    <div
                      className={`answer-status ${
                        selectedAnswers[currentMcqIndex] === currentMcq?.correctAnswer ? "correct" : "incorrect"
                      }`}
                    >
                      {selectedAnswers[currentMcqIndex] === currentMcq?.correctAnswer ? (
                        <>
                          <CheckCircle size={16} />
                          Correct Answer
                        </>
                      ) : (
                        <>
                          <XCircle size={16} />
                          Incorrect Answer
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="question-content">
                  <h3 className="question-text">{currentMcq?.question}</h3>
                  <div className="options-container">
                    {[currentMcq?.option1, currentMcq?.option2, currentMcq?.option3, currentMcq?.option4].map(
                      (option, optionIndex) => {
                        const canSelect = !showAnswers && selectedAnswers[currentMcqIndex] === null
                        return (
                          <div
                            key={optionIndex}
                            className={getOptionClassName(currentMcqIndex, option)}
                            onClick={() => handleAnswerSelect(currentMcqIndex, option)}
                            style={{
                              cursor: canSelect ? "pointer" : "not-allowed",
                              opacity: !canSelect && !showAnswers ? 0.6 : 1,
                            }}
                          >
                            <div className="option-content">
                              <div className="option-letter">{String.fromCharCode(65 + optionIndex)}</div>
                              <span className="option-text">{option}</span>
                            </div>
                            {showAnswers && (
                              <div className="option-status">
                                {option === currentMcq?.correctAnswer && (
                                  <CheckCircle size={20} className="correct-icon" />
                                )}
                                {option === selectedAnswers[currentMcqIndex] &&
                                  option !== currentMcq?.correctAnswer && (
                                    <XCircle size={20} className="incorrect-icon" />
                                  )}
                              </div>
                            )}
                          </div>
                        )
                      },
                    )}
                  </div>
                  {showAnswers && (
                    <div className="answer-explanation">
                      <div className="explanation-header">
                        <CheckCircle size={20} />
                        <h6>Answer Explanation</h6>
                      </div>
                      <div className="explanation-content">
                        <p>
                          <strong>Correct Answer:</strong> {currentMcq?.correctAnswer}
                        </p>
                        {selectedAnswers[currentMcqIndex] && (
                          <p>
                            <strong>Your Answer:</strong> {selectedAnswers[currentMcqIndex]}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="question-footer">
                  <Button className="nav-btn prev-btn" onClick={handlePreviousMcq} disabled={currentMcqIndex === 0}>
                    <ChevronLeft size={16} />
                    Previous
                  </Button>
                  <div className="question-indicator">
                    <span
                      className={`indicator-dot ${selectedAnswers[currentMcqIndex] ? "answered" : "unanswered"}`}
                    ></span>
                    <span className="indicator-text">
                      {selectedAnswers[currentMcqIndex] ? "Answered" : "Not answered"}
                    </span>
                  </div>
                  <Button
                    className="nav-btn next-btn"
                    onClick={handleNextMcq}
                    disabled={currentMcqIndex === mcqs.length - 1}
                  >
                    Next
                    <ChevronLeft size={16} style={{ transform: "rotate(180deg)" }} />
                  </Button>
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Container>

      <style>{`
        .mcq-container {
          min-height: 100vh;
        }
        .loading-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .loading-content {
          text-align: center;
          padding: 3rem;
        }
        .loading-spinner {
          position: relative;
          margin: 0 auto 2rem;
          width: 80px;
          height: 80px;
        }
        .spinner-ring {
          position: absolute;
          width: 80px;
          height: 80px;
          border: 4px solid #fed7aa;
          border-top: 4px solid #ea580c;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        .brain-icon {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: #ea580c;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .loading-title {
          color: #9a3412;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }
        .loading-subtitle {
          color: #c2410c;
          margin-bottom: 2rem;
        }
        .loading-progress {
          width: 200px;
          height: 4px;
          background: #fed7aa;
          border-radius: 2px;
          margin: 0 auto;
          overflow: hidden;
        }
        .progress-bar {
          height: 100%;
          background: linear-gradient(90deg, #ea580c, #f97316);
          border-radius: 2px;
          animation: progressMove 2s ease-in-out infinite;
        }
        @keyframes progressMove {
          0% { width: 0%; }
          50% { width: 100%; }
          100% { width: 0%; }
        }
        .error-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .error-content {
          text-align: center;
          padding: 3rem;
          background: white;
          border-radius: 20px;
          box-shadow: 0 20px 40px rgba(234, 88, 12, 0.1);
          max-width: 500px;
        }
        .error-icon {
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, #fed7aa, #ffedd5);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          color: #ea580c;
        }
        .error-title {
          color: #9a3412;
          font-weight: 700;
          margin-bottom: 1rem;
        }
        .error-message {
          color: #c2410c;
          margin-bottom: 2rem;
          font-size: 1.1rem;
        }
        .error-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }
        .btn-primary-custom {
          background: linear-gradient(135deg, #ea580c, #f97316);
          border: none;
          border-radius: 12px;
          padding: 0.75rem 2rem;
          font-weight: 600;
          color: white;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
        }
        .btn-primary-custom:hover {
          background: linear-gradient(135deg, #dc2626, #ea580c);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(234, 88, 12, 0.3);
        }
        .btn-outline-custom {
          background: transparent;
          border: 2px solid #ea580c;
          border-radius: 12px;
          padding: 0.75rem 2rem;
          font-weight: 600;
          color: #ea580c;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
        }
        .btn-outline-custom:hover {
          background: #ea580c;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(234, 88, 12, 0.3);
        }
        .header-section {
          background: linear-gradient(135deg, #ea580c, #f97316);
          color: white;
          padding: 1.5rem 0;
          box-shadow: 0 4px 20px rgba(234, 88, 12, 0.2);
        }
        .header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .back-btn {
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white;
          border-radius: 10px;
          padding: 0.5rem 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
        }
        .back-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          color: white;
          transform: translateX(-2px);
        }
        .course-info {
          margin: 0;
        }
        .course-title {
          margin: 0;
          font-weight: 700;
          font-size: 1.5rem;
        }
        .course-subtitle {
          margin: 0;
          opacity: 0.9;
          display: flex;
          align-items: center;
          font-size: 0.9rem;
        }
        .header-stats {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          flex-wrap: wrap;
        }
        .stat-item {
          background: rgba(255, 255, 255, 0.2);
          padding: 0.5rem 1rem;
          border-radius: 10px;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          font-weight: 600;
          backdrop-filter: blur(10px);
        }
        .timer-display {
          background: rgba(255, 255, 255, 0.3);
          font-weight: 700;
        }
        .timer-warning {
          color: #fbbf24 !important;
          animation: pulse 1s infinite;
        }
        .score-badge {
          background: rgba(34, 197, 94, 0.3);
          color: white;
        }
        .main-content {
          padding: 2rem 0;
        }
        .sidebar-card {
          background: white;
          border-radius: 20px;
          box-shadow: 0 8px 32px rgba(234, 88, 12, 0.1);
          overflow: hidden;
          position: sticky;
          top: 2rem;
        }
        .sidebar-header {
          background: linear-gradient(135deg, #f97316, #ea580c);
          color: white;
          padding: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        .sidebar-header h6 {
          margin: 0;
          font-weight: 700;
          font-size: 1rem;
        }
        .progress-overview {
          padding: 1.5rem;
          border-bottom: 1px solid #fed7aa;
        }
        .progress-stats {
          display: flex;
          justify-content: space-between;
          margin-bottom: 1rem;
        }
        .stat {
          text-align: center;
        }
        .stat-number {
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
          color: #ea580c;
        }
        .stat-label {
          font-size: 0.8rem;
          color: #9a3412;
        }
        .progress-bar-container {
          height: 8px;
          background: #fed7aa;
          border-radius: 4px;
          overflow: hidden;
        }
        .progress-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #ea580c, #f97316);
          border-radius: 4px;
          transition: width 0.3s ease;
        }
        .questions-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(45px, 1fr));
          gap: 0.75rem;
          padding: 1.5rem;
          max-height: 300px;
          overflow-y: auto;
        }
        .question-btn {
          width: 45px;
          height: 45px;
          border: none;
          border-radius: 12px;
          position: relative;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.9rem;
        }
        .question-btn.unanswered {
          background: #f3f4f6;
          color: #6b7280;
        }
        .question-btn.answered {
          background: #dbeafe;
          color: #1d4ed8;
        }
        .question-btn.correct {
          background: #dcfce7;
          color: #16a34a;
        }
        .question-btn.incorrect {
          background: #fecaca;
          color: #dc2626;
        }
        .question-btn.current {
          background: linear-gradient(135deg, #ea580c, #f97316);
          color: white;
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
        }
        .question-btn.locked {
          background: #e5e7eb;
          color: #9ca3af;
          cursor: not-allowed;
          opacity: 0.5;
        }
        .question-btn:hover:not(.locked) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .question-btn.current:hover {
          transform: scale(1.1) translateY(-2px);
        }
        .question-number {
          font-size: 0.9rem;
        }
        .question-status {
          position: absolute;
          top: -4px;
          right: -4px;
          background: white;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .sidebar-actions {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .action-btn {
          width: 100%;
          padding: 0.75rem;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .reset-btn {
          background: linear-gradient(135deg, #f59e0b, #fbbf24);
          color: white;
        }
        .finish-btn {
          background: linear-gradient(135deg, #8b5cf6, #a855f7);
          color: white;
        }
        .submit-btn {
          background: linear-gradient(135deg, #ea580c, #f97316);
          color: white;
        }
        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .question-card {
          background: white;
          border-radius: 20px;
          box-shadow: 0 8px 32px rgba(234, 88, 12, 0.1);
          overflow: hidden;
        }
        .score-card {
          background: white;
          border-radius: 20px;
          box-shadow: 0 8px 32px rgba(234, 88, 12, 0.1);
          overflow: hidden;
          padding: 2rem;
        }
        .score-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
        }
        .score-header h3 {
          margin: 0;
          color: #9a3412;
          font-weight: 700;
        }
        .score-content {
          margin-bottom: 2rem;
        }
        .score-metric {
          margin-bottom: 1.5rem;
          text-align: center;
        }
        .score-value {
          font-size: 3rem;
          font-weight: 700;
          color: #ea580c;
          line-height: 1;
        }
        .score-label {
          font-size: 1rem;
          color: #c2410c;
        }
        .score-details {
          display: flex;
          justify-content: center;
          gap: 1.5rem;
          flex-wrap: wrap;
          margin-bottom: 2rem;
        }
        .detail-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          border-radius: 8px;
          font-weight: 600;
        }
        .detail-item.correct {
          background: #dcfce7;
          color: #16a34a;
        }
        .detail-item.incorrect {
          background: #fecaca;
          color: #dc2626;
        }
        .detail-item.total {
          background: #dbeafe;
          color: #1d4ed8;
        }
        /* Results List Styles */
        .results-list {
          margin-top: 2rem;
          max-height: 400px;
          overflow-y: auto;
          padding-right: 1rem;
        }
        .result-item {
          padding: 1.5rem;
          margin-bottom: 1rem;
          border-radius: 12px;
          background: #f8fafc;
          border-left: 4px solid #e2e8f0;
        }
        .result-item.correct {
          border-left-color: #16a34a;
          background: #f0fdf4;
        }
        .result-item.incorrect {
          border-left-color: #dc2626;
          background: #fef2f2;
        }
        .result-question {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1rem;
          font-weight: 600;
        }
        .result-number {
          color: #4b5563;
        }
        .result-text {
          color: #1f2937;
        }
        .result-answers {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .result-answer {
          display: flex;
          gap: 0.5rem;
          align-items: baseline;
        }
        .answer-label {
          font-weight: 600;
          color: #4b5563;
          min-width: 100px;
        }
        .answer-value {
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-weight: 500;
        }
        .answer-value.wrong {
          background: #fee2e2;
          color: #b91c1c;
        }
        .answer-value.correct {
          background: #dcfce7;
          color: #166534;
        }
        .score-actions {
          display: flex;
          justify-content: center;
        }
        .restart-btn {
          background: linear-gradient(135deg, #ea580c, #f97316);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
        }
        .restart-btn:hover {
          background: linear-gradient(135deg, #dc2626, #ea580c);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(234, 88, 12, 0.3);
        }
        .question-header {
          background: linear-gradient(135deg, #fff7ed, #fed7aa);
          padding: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #fed7aa;
        }
        .question-meta {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .question-label {
          font-weight: 700;
          color: #9a3412;
          font-size: 1.1rem;
        }
        .difficulty-badge {
          background: rgba(234, 88, 12, 0.1);
          color: #ea580c;
          padding: 0.5rem 1rem;
          border-radius: 10px;
          font-size: 0.8rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .answer-status {
          padding: 0.5rem 1rem;
          border-radius: 10px;
          font-size: 0.9rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .answer-status.correct {
          background: rgba(34, 197, 94, 0.1);
          color: #16a34a;
        }
        .answer-status.incorrect {
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
        }
        .question-content {
          padding: 2rem;
        }
        .question-text {
          color: #1f2937;
          font-weight: 700;
          margin-bottom: 2rem;
          line-height: 1.6;
          font-size: 1.3rem;
        }
        .options-container {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 2rem;
        }
        .option-card {
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 16px;
          padding: 1.25rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .option-card:hover {
          background: #f1f5f9;
          border-color: #ea580c;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(234, 88, 12, 0.1);
        }
        .option-selected {
          background: linear-gradient(135deg, #fff7ed, #fed7aa);
          border-color: #ea580c;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(234, 88, 12, 0.2);
        }
        .option-correct {
          background: linear-gradient(135deg, #f0fdf4, #dcfce7);
          border-color: #16a34a;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(34, 197, 94, 0.2);
        }
        .option-incorrect {
          background: linear-gradient(135deg, #fef2f2, #fecaca);
          border-color: #dc2626;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(239, 68, 68, 0.2);
        }
        .option-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .option-letter {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #ea580c, #f97316);
          color: white;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.1rem;
          flex-shrink: 0;
        }
        .option-correct .option-letter {
          background: linear-gradient(135deg, #16a34a, #22c55e);
        }
        .option-incorrect .option-letter {
          background: linear-gradient(135deg, #dc2626, #ef4444);
        }
        .option-text {
          font-size: 1.1rem;
          color: #374151;
          font-weight: 500;
          line-height: 1.5;
        }
        .option-status {
          flex-shrink: 0;
        }
        .correct-icon {
          color: #16a34a;
        }
        .incorrect-icon {
          color: #dc2626;
        }
        .answer-explanation {
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border: 1px solid #3b82f6;
          border-radius: 16px;
          padding: 1.5rem;
          margin-top: 1rem;
        }
        .explanation-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
          color: #1d4ed8;
        }
        .explanation-header h6 {
          margin: 0;
          font-weight: 700;
        }
        .explanation-content p {
          margin: 0.5rem 0;
          color: #1e40af;
          font-weight: 500;
        }
        .question-footer {
          background: #f8fafc;
          padding: 1.5rem 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-top: 1px solid #e2e8f0;
        }
        .nav-btn {
          background: linear-gradient(135deg, #ea580c, #f97316);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .nav-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #dc2626, #ea580c);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(234, 88, 12, 0.3);
        }
        .nav-btn:disabled {
          background: #d1d5db;
          color: #9ca3af;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }
        .prev-btn:disabled {
          background: #f3f4f6;
          color: #9ca3af;
        }
        .next-btn:disabled {
          background: #f3f4f6;
          color: #9ca3af;
        }
        .question-indicator {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 1rem;
          background: white;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
        }
        .indicator-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          transition: all 0.3s ease;
        }
        .indicator-dot.answered {
          background: linear-gradient(135deg, #16a34a, #22c55e);
          box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
        }
        .indicator-dot.unanswered {
          background: #d1d5db;
        }
        .indicator-text {
          font-size: 0.9rem;
          color: #6b7280;
          font-weight: 500;
        }
        /* Responsive Design */
        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            gap: 1rem;
            text-align: center;
          }
          .header-stats {
            justify-content: center;
          }
          .question-text {
            font-size: 1.1rem;
          }
          .option-letter {
            width: 35px;
            height: 35px;
            font-size: 1rem;
          }
          .option-text {
            font-size: 1rem;
          }
          .question-footer {
            flex-direction: column;
            gap: 1rem;
          }
          .nav-btn {
            width: 100%;
            justify-content: center;
          }
          .questions-grid {
            grid-template-columns: repeat(auto-fit, minmax(40px, 1fr));
            gap: 0.5rem;
          }
          .question-btn {
            width: 40px;
            height: 40px;
          }
          .result-item {
            padding: 1rem;
          }
          .result-question {
            flex-direction: column;
          }
          .answer-label {
            min-width: 80px;
          }
        }
        /* Smooth Scrolling */
        .questions-grid {
          scrollbar-width: thin;
          scrollbar-color: #ea580c #fed7aa;
        }
        .questions-grid::-webkit-scrollbar {
          width: 6px;
        }
        .questions-grid::-webkit-scrollbar-track {
          background: #fed7aa;
          border-radius: 3px;
        }
        .questions-grid::-webkit-scrollbar-thumb {
          background: #ea580c;
          border-radius: 3px;
        }
        .questions-grid::-webkit-scrollbar-thumb:hover {
          background: #dc2626;
        }
        .results-list {
          scrollbar-width: thin;
          scrollbar-color: #ea580c #fed7aa;
        }
        .results-list::-webkit-scrollbar {
          width: 6px;
        }
        .results-list::-webkit-scrollbar-track {
          background: #fed7aa;
          border-radius: 3px;
        }
        .results-list::-webkit-scrollbar-thumb {
          background: #ea580c;
          border-radius: 3px;
        }
        .results-list::-webkit-scrollbar-thumb:hover {
          background: #dc2626;
        }
        /* Animation for new questions */
        @keyframes questionSlideIn {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .question-card {
          animation: questionSlideIn 0.5s ease-out;
        }
        /* Pulse animation for current question */
        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
          }
          50% {
            box-shadow: 0 8px 20px rgba(234, 88, 12, 0.5);
          }
        }
        .question-btn.current {
          animation: pulse 2s infinite;
        }
        /* Hover effects for better UX */
        .sidebar-card {
          transition: all 0.3s ease;
        }
        .sidebar-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(234, 88, 12, 0.15);
        }
        .question-card {
          transition: all 0.3s ease;
        }
        .question-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(234, 88, 12, 0.15);
        }
        /* Focus states for accessibility */
        .question-btn:focus,
        .nav-btn:focus,
        .action-btn:focus {
          outline: 2px solid #ea580c;
          outline-offset: 2px;
        }
        .option-card:focus {
          outline: 2px solid #ea580c;
          outline-offset: 2px;
        }
      `}</style>
    </div>
  )
}

export default StudentMcqView


